import React from "react";
import { Box } from "@mui/material";
import {
  certificatePageSize,
  certificatePaperColour,
  type CertificateOrientation,
} from "../../../domain/certificates/certificateTemplates";
import { CentredLayout, type CertificateDesign } from "./certificateParts";

/** The primary-coloured band round the page edge. */
const OUTER_BAND = 22;
/** The accent band inside it. */
const INNER_BAND = 18;
const PANEL_INSET = OUTER_BAND + INNER_BAND;

/** The books' cover colours, bottom to top, after the template's own two. */
const BOOK_RED = "#d9534f";
const BOOK_GREEN = "#3a9d5d";
const BOOK_ORANGE = "#e8833a";
const PAGE_EDGES = "#fbf3df";

/** How the stack of books sits in the bottom-left corner, for each orientation. */
const STACK: Record<CertificateOrientation, { scale: number }> = {
  landscape: { scale: 1 },
  portrait: { scale: 0.85 },
};

/** One book lying flat, its spine facing out, drawn from its top-left corner. */
const Book: React.FC<{
  x: number;
  y: number;
  width: number;
  height: number;
  cover: string;
}> = ({ x, y, width, height, cover }) => (
  <g transform={`translate(${x} ${y})`}>
    {/* The pages, showing at the right-hand end. */}
    <rect x={width - 14} y={3} width={14} height={height - 6} rx={2} fill={PAGE_EDGES} />
    {[0.3, 0.5, 0.7].map((f) => (
      <line
        key={f}
        x1={width - 13}
        x2={width - 1}
        y1={height * f}
        y2={height * f}
        stroke="#d9cdb0"
        strokeWidth={1}
      />
    ))}
    <rect width={width - 10} height={height} rx={4} fill={cover} />
    {/* Bands across the spine. */}
    <rect x={14} y={0} width={6} height={height} fill="#ffffff" opacity={0.35} />
    <rect x={width - 34} y={0} width={6} height={height} fill="#ffffff" opacity={0.35} />
    <rect
      x={0}
      y={0}
      width={width - 10}
      height={height}
      rx={4}
      fill="none"
      stroke="#000000"
      strokeOpacity={0.18}
      strokeWidth={1.5}
    />
  </g>
);

/** A stack of six books, a little askew, coloured from the template's colours and a few bright ones. */
const BookStack: React.FC<{
  pageHeight: number;
  scale: number;
  primary: string;
  accent: string;
}> = ({ pageHeight, scale, primary, accent }) => {
  const books = [
    { width: 196, height: 30, offset: 0, cover: primary },
    { width: 176, height: 26, offset: 12, cover: BOOK_RED },
    { width: 186, height: 28, offset: 4, cover: accent },
    { width: 168, height: 24, offset: 18, cover: BOOK_GREEN },
    { width: 180, height: 28, offset: 8, cover: BOOK_ORANGE },
    { width: 160, height: 24, offset: 22, cover: primary },
  ];
  const stackHeight = books.reduce((sum, book) => sum + book.height, 0);
  let top = stackHeight;
  return (
    <g transform={`translate(12 ${pageHeight - 12 - stackHeight * scale}) scale(${scale})`}>
      {books.map((book, i) => {
        top -= book.height;
        return <Book key={i} x={book.offset} y={top} {...book} />;
      })}
    </g>
  );
};

/** A pencil pointing down-left, drawn along the x-axis with its tip at the origin. */
const Pencil: React.FC<{ transform: string; body: string }> = ({ transform, body }) => (
  <g transform={transform}>
    {/* Tip: graphite, then the sharpened wood. */}
    <polygon points="0,0 34,-14 34,14" fill="#f1cf9b" />
    <polygon points="0,0 12,-5 12,5" fill="#3d3d3d" />
    {/* Body, with a darker facet along one side. */}
    <rect x={34} y={-14} width={120} height={28} fill={body} />
    <rect x={34} y={4} width={120} height={10} fill="#000000" opacity={0.12} />
    {/* Metal ferrule and the eraser. */}
    <rect x={154} y={-15} width={22} height={30} fill="#b8bcc4" />
    <line x1={162} x2={162} y1={-15} y2={15} stroke="#8c919a" strokeWidth={2} />
    <line x1={169} x2={169} y1={-15} y2={15} stroke="#8c919a" strokeWidth={2} />
    <rect x={176} y={-15} width={26} height={30} rx={8} fill="#f28b9b" />
  </g>
);

/**
 * Schoolbooks: a primary band round the page and an accent band inside it,
 * with a stack of books in the bottom-left corner and a pencil over the
 * top-right one, like a primary school's certificate of achievement. The
 * Awardee's name is written in a script.
 */
export const schoolbooksDesign: CertificateDesign = {
  bodyFont: "lora",
  Layout: ({ template, text, orientation }) => {
    const { primaryColour: primary, accentColour: accent } = template;
    const { width, height } = certificatePageSize(orientation);
    return (
      <CentredLayout
        template={template}
        text={text}
        orientation={orientation}
        lettering={{ nameFont: "great-vibes", nameColour: primary }}
        inset={{
          // Clear of the books on the left and the pencil on the right.
          landscape: "70px 220px 64px",
          // The footer sits above the books.
          portrait: "96px 76px 200px",
        }}
        frame={
          <Box
            component="svg"
            viewBox={`0 0 ${width} ${height}`}
            aria-hidden="true"
            sx={{ position: "absolute", inset: 0, width, height }}
          >
            <rect width={width} height={height} fill={primary} />
            <rect
              x={OUTER_BAND}
              y={OUTER_BAND}
              width={width - OUTER_BAND * 2}
              height={height - OUTER_BAND * 2}
              fill={accent}
            />
            <rect
              x={PANEL_INSET}
              y={PANEL_INSET}
              width={width - PANEL_INSET * 2}
              height={height - PANEL_INSET * 2}
              fill={certificatePaperColour(template)}
              stroke="#000000"
              strokeOpacity={0.2}
              strokeWidth={2}
            />
            <Pencil
              transform={`translate(${width - 150} 112) rotate(-40)`}
              body={accent}
            />
            <BookStack
              pageHeight={height}
              scale={STACK[orientation].scale}
              primary={primary}
              accent={accent}
            />
          </Box>
        }
      />
    );
  },
};
